import { json } from '@codemirror/lang-json';
import { Extension } from '@codemirror/state';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Checkbox, LinearProgress } from '@mui/joy';
import { PopoverContent } from '@radix-ui/react-popover';

import { StarsIcon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { gatherSecrets } from '@/Pages/Playground/components/PlaygroundSecrets.utils';
import { DEFAULT_MODEL } from '@/Pages/Playground/components/manifest/serialized/chat_models/ManifestChatOpenAI.utils';
import { EvaluatorSecrets } from '@/Pages/Settings/components/EvaluatorSecrets';
import { useTenantSecretSet } from '@/Pages/Settings/utils/useTenantSecretSet';
import {
  Badge,
  Button,
  GroupedTabs,
  Input,
  Text,
  Tooltip,
} from '@/design-system';
import { cn } from '@/utils/tailwind';
import {
  jsonSchemaExtensions,
  useCodeMirrorTooltipFollowMouse,
} from '@/utils/use-codemirror-tooltip-follow-mouse';

import { useBulkExampleCreate } from '../../hooks/useSwr';
import { DatasetSchema, GeneratedExampleIO } from '../../types/schema';
import { Code, CodeCard } from '../Code/Code';
import { Pane } from '../Pane';
import { Popover, PopoverTrigger } from '../Popover';
import useToast from '../Toast/useToast';
import { useGenerateSyntheticExamples } from './hooks/useGenerateSyntheticExamples';

const HeaderItem = ({
  title,
  tooltipContent,
}: {
  title: string;
  tooltipContent: string;
}) => {
  return (
    <div className="mr-8 flex justify-between">
      <span className="my-auto px-2 font-medium uppercase">{title}</span>
      <Tooltip
        title={
          <div className="max-h-[400px] max-w-[400px] overflow-auto whitespace-pre-wrap">
            {tooltipContent}
          </div>
        }
      >
        <div className="my-auto flex cursor-pointer flex-row gap-2">
          <span className="text-sm">Schema</span>
          <InformationCircleIcon className="my-auto h-4 w-4" />
        </div>
      </Tooltip>
    </div>
  );
};

export function SyntheticExamplesPane({
  isOpen,
  doClose,
  dataset,
  onMutate,
  selectedExampleIds,
  numExamplesToGenerate,
  selectionMethod,
}: {
  isOpen: boolean;
  doClose: () => void;
  dataset: DatasetSchema;
  onMutate?: () => void;
  selectedExampleIds: string[];
  selectionMethod: 'automatic' | 'manual';
  numExamplesToGenerate: number;
}) {
  const [selectedGeneratedExampleIds, setSelectedGeneratedExampleIds] =
    useState(new Set<number>());

  // Synthetic Example Generation
  const {
    syntheticExamples,
    isGenerateLoading,
    generateTrigger,
    setSyntheticExamples,
  } = useGenerateSyntheticExamples(
    selectedExampleIds,
    numExamplesToGenerate,
    selectionMethod,
    dataset.id
  );

  const handleCheckboxChange = useCallback((exampleId: number) => {
    setSelectedGeneratedExampleIds((prevSet) => {
      const newSet = new Set(prevSet);
      if (newSet.has(exampleId)) {
        newSet.delete(exampleId);
      } else {
        newSet.add(exampleId);
      }
      return newSet;
    });
  }, []);

  const onInputChange = useCallback(
    (newInput, exampleId) => {
      const updatedExamples = syntheticExamples.map((e) =>
        e.id === exampleId ? { ...e, inputs: JSON.parse(newInput) } : e
      );
      setSyntheticExamples(updatedExamples);
    },
    [syntheticExamples, setSyntheticExamples]
  );

  const onOutputChange = useCallback(
    (newOutput, exampleId) => {
      const updatedExamples = syntheticExamples.map((e) =>
        e.id === exampleId ? { ...e, outputs: JSON.parse(newOutput) } : e
      );
      setSyntheticExamples(updatedExamples);
    },
    [syntheticExamples, setSyntheticExamples]
  );

  const { createToast } = useToast();

  const { isMutating: isSubmitExamplesLoading, trigger: onSubmit } =
    useBulkExampleCreate({
      datasetDataType: dataset.data_type,
      onSuccess: () => {
        doClose();
        onMutate?.();
        createToast({
          title: 'Success',
          description: 'Example added to dataset',
        });
      },
      onError: () => {
        createToast({
          title: 'Error',
          description: 'Failed to add examples to dataset',
          error: true,
        });
      },
    });

  useEffect(() => {
    if (isOpen) {
      setSelectedGeneratedExampleIds(new Set());
      if (syntheticExamples.length === 0) {
        generateTrigger();
      }
    } else {
      setSyntheticExamples([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    doClose();
    setSyntheticExamples([]);
  }, [doClose, setSyntheticExamples]);

  return (
    <Pane
      open={isOpen}
      onClose={handleClose}
      title={'Select generated examples'}
      noBackArrow
      className="relative px-0 pt-0"
      topBarRightElement={
        <div className="mr-5 flex items-center gap-2">
          <Button
            type="button"
            onClick={doClose}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            disabled={isGenerateLoading}
            onClick={() => generateTrigger()}
          >
            Regenerate examples
          </Button>
          <Button
            type={'submit'}
            loading={isSubmitExamplesLoading}
            disabled={
              isSubmitExamplesLoading ||
              selectedGeneratedExampleIds.size === 0 ||
              isGenerateLoading
            }
            onClick={() => {
              const stringifiedExamples = syntheticExamples
                .filter((e) => selectedGeneratedExampleIds.has(e.id))
                .map((e) => {
                  return {
                    inputs: JSON.stringify(e.inputs),
                    outputs: JSON.stringify(e.outputs),
                    metadata: JSON.stringify({ source: 'synthetic' }),
                  };
                });
              onSubmit({
                dataset_id: dataset.id,
                examples: stringifiedExamples,
              });
            }}
            color="primary"
          >
            {`Save ${
              selectedGeneratedExampleIds.size > 0
                ? selectedGeneratedExampleIds.size
                : ''
            } Example${selectedGeneratedExampleIds.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div className="mb-4 grid grid-cols-12 bg-secondary px-2">
        <div className="col-span-1 flex h-10 flex-row gap-2">
          {isGenerateLoading ? null : (
            <Checkbox
              className="my-auto ml-4"
              checked={
                selectedGeneratedExampleIds.size === syntheticExamples.length &&
                syntheticExamples.length > 0
              }
              onChange={() => {
                setSelectedGeneratedExampleIds((prevSet) => {
                  if (prevSet.size === syntheticExamples.length) {
                    return new Set();
                  } else {
                    return new Set(syntheticExamples.map((e) => e.id));
                  }
                });
              }}
            />
          )}
        </div>
        <div className="col-span-11 grid grid-cols-2">
          <HeaderItem
            title="Input"
            tooltipContent={JSON.stringify(
              dataset.inputs_schema_definition,
              null,
              2
            )}
          />
          <HeaderItem
            title="Output"
            tooltipContent={JSON.stringify(
              dataset.outputs_schema_definition,
              null,
              2
            )}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 px-2">
        {syntheticExamples.map((example) => (
          <SyntheticExample
            key={example.id}
            example={example}
            datasetId={dataset.id}
            isSelected={selectedGeneratedExampleIds.has(example.id)}
            onCheckboxChange={() => handleCheckboxChange(example.id)}
            onInputChange={(newInputs) => onInputChange(newInputs, example.id)}
            onOutputChange={(newOutputs) =>
              onOutputChange(newOutputs, example.id)
            }
            inputSchemaDefinition={dataset.inputs_schema_definition}
            outputSchemaDefinition={dataset.outputs_schema_definition}
          />
        ))}
        {isGenerateLoading && <LinearProgress />}
      </div>
    </Pane>
  );
}

const SyntheticExample = React.memo(
  ({
    example,
    datasetId,
    isSelected,
    onCheckboxChange,
    onInputChange,
    onOutputChange,
    inputSchemaDefinition,
    outputSchemaDefinition,
  }: {
    example: GeneratedExampleIO;
    datasetId: string;
    isSelected: boolean;
    onCheckboxChange: () => void;
    onInputChange: (newInputs: string) => void;
    onOutputChange: (newOutputs: string) => void;
    inputSchemaDefinition: DatasetSchema['inputs_schema_definition'];
    outputSchemaDefinition: DatasetSchema['outputs_schema_definition'];
  }) => {
    const memoizedInputs = useMemo(
      () => JSON.stringify(example.inputs, null, 2),
      [example.inputs]
    );

    const memoizedOutputs = useMemo(
      () => JSON.stringify(example.outputs, null, 2),
      [example.outputs]
    );

    const inputElementRef = useRef<HTMLDivElement>(null);
    const outputElementRef = useRef<HTMLDivElement>(null);

    useCodeMirrorTooltipFollowMouse(
      inputElementRef,
      outputElementRef,
      datasetId
    );

    const inputChangeDebounced = useDebouncedCallback(onInputChange, 500);
    const outputChangeDebounced = useDebouncedCallback(onOutputChange, 500);

    const inputSchemaJsonExtensions = useMemo(() => {
      return jsonSchemaExtensions(inputSchemaDefinition);
    }, [inputSchemaDefinition]);

    const outputSchemaJsonExtensions = useMemo(() => {
      return jsonSchemaExtensions(outputSchemaDefinition);
    }, [outputSchemaDefinition]);

    return (
      <div
        key={example.id}
        className={cn(
          'grid grid-cols-12 rounded-md py-2',
          isSelected ? 'bg-brand-green-400/5' : ''
        )}
      >
        <div className="col-span-1 mb-auto flex flex-row gap-2">
          <Checkbox
            checked={isSelected}
            onChange={onCheckboxChange}
            className="ml-4"
          />
          <div className="font-bold">#{example.id + 1}</div>
        </div>
        <div className="col-span-11 grid grid-cols-2 gap-8">
          <CodeEditor
            ref={inputElementRef}
            value={memoizedInputs}
            onChange={inputChangeDebounced}
            extensions={inputSchemaJsonExtensions}
          />
          <CodeEditor
            ref={outputElementRef}
            value={memoizedOutputs}
            onChange={outputChangeDebounced}
            extensions={outputSchemaJsonExtensions}
          />
        </div>
      </div>
    );
  }
);

const CodeEditor = ({
  ref,
  value,
  onChange,
  extensions,
}: {
  ref: React.RefObject<HTMLDivElement>;
  value: string;
  onChange: (newValue: string) => void;
  extensions: Extension[];
}) => {
  return (
    <CodeCard>
      <div ref={ref}>
        <Code
          language={json()}
          value={value}
          onChange={onChange}
          extensions={extensions}
        />
      </div>
    </CodeCard>
  );
};

const buttonStyle = cn(
  'flex flex-shrink-0 items-center rounded-md border border-secondary px-2 py-1 text-sm',
  'font-semibold text-tertiary transition-all hover:bg-secondary-hover active:bg-secondary data-[state=open]:bg-secondary',
  'cursor-pointer border-brand-subtle bg-brand-tertiary text-brand-primary hover:bg-brand-secondary active:bg-brand-tertiary data-[state=open]:bg-brand-primary'
);

export const GenerateSyntheticExamplesButton = (props: {
  disabled: boolean;
  selectionMethod: 'automatic' | 'manual';
  setSelectionMethod: (value: 'automatic' | 'manual') => void;
  selectedExampleIds: string[];
  setSelectedExampleIds: (value: string[]) => void;
  setIsSyntheticExamplesPaneOpen: (value: boolean) => void;
  numExamplesToGenerate: number;
  setNumExamplesToGenerate: (value: number) => void;
  generateTrigger: () => void;
}) => {
  const [isNumberSelectorOpen, setIsNumberSelectorOpen] = useState(false);

  return (
    <Popover
      open={isNumberSelectorOpen}
      onOpenChange={(open) => {
        setIsNumberSelectorOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            buttonStyle,
            'gap-1/2',
            props.disabled && 'cursor-auto opacity-70 hover:bg-brand-tertiary'
          )}
          disabled={props.disabled}
        >
          <StarsIcon className={cn('mr-2 h-4 w-4')} />
          Generate Examples
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={5}
        className="z-10 rounded-lg border border-secondary bg-popover shadow-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <GenerateSyntheticExamplesPopoverContent
          {...props}
          setIsNumberSelectorOpen={setIsNumberSelectorOpen}
        />
      </PopoverContent>
    </Popover>
  );
};

export const GenerateSyntheticExamplesPopoverContent = ({
  setIsNumberSelectorOpen,
  setIsSyntheticExamplesPaneOpen,
  selectionMethod,
  setSelectionMethod,
  selectedExampleIds,
  numExamplesToGenerate,
  setNumExamplesToGenerate,
}: {
  setIsNumberSelectorOpen: (value: boolean) => void;
  setIsSyntheticExamplesPaneOpen: (value: boolean) => void;
  selectionMethod: 'automatic' | 'manual';
  setSelectionMethod: (value: 'automatic' | 'manual') => void;
  selectedExampleIds: string[];
  numExamplesToGenerate: number;
  setNumExamplesToGenerate: (value: number) => void;
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleNumberChange = useCallback(
    (value: string) => {
      const num = parseInt(value);
      setNumExamplesToGenerate(num);
      if (num > 10) {
        setError('Number of examples cannot exceed 10');
      } else if (num < 1) {
        setError('Number of examples must be at least 1');
      } else {
        setError(null);
      }
    },
    [setNumExamplesToGenerate]
  );

  const isWrongNumExamplesToGenerate =
    numExamplesToGenerate > 10 || numExamplesToGenerate < 1;
  const isWrongNumReferenceExamples =
    selectionMethod === 'manual' &&
    (selectedExampleIds.length === 0 || selectedExampleIds.length > 10);

  const isGenerateDisabled =
    isWrongNumExamplesToGenerate || isWrongNumReferenceExamples;

  const disabledMessage = isWrongNumExamplesToGenerate
    ? 'Number of examples must be between 1 and 10'
    : isWrongNumReferenceExamples
    ? 'Number of reference examples must be between 1 and 10'
    : undefined;

  const openSecretsRef = useRef<(() => void) | null>(null);
  const secretKeys = useTenantSecretSet();
  const model: SerializedConstructor = {
    lc: 1,
    type: 'constructor',
    id: ['langchain', 'chat_models', 'openai', 'ChatOpenAI'],
    kwargs: {
      model: DEFAULT_MODEL,
      openai_api_key: {
        id: ['OPENAI_API_KEY'],
        lc: 1,
        type: 'secret',
      },
    },
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-start justify-between gap-1">
        <div className="flex flex-col gap-2">
          <Text variant="h3">Generate examples</Text>
          <Text>
            Use automatically-selected reference examples or select them
            manually
          </Text>
        </div>
        <EvaluatorSecrets
          model={model}
          bypassPortal
          openSecretsRef={openSecretsRef}
          infoMessage="Your API keys are stored encrypted on our servers."
          triggerComponent={
            <Button variant="underlined" size="sm">
              API Key
            </Button>
          }
        />
      </div>
      <GroupedTabs
        value={selectionMethod}
        onChange={(value) =>
          setSelectionMethod(value as 'automatic' | 'manual')
        }
        options={[
          {
            value: 'automatic',
            display: 'Automatic',
            className: 'w-1/2 justify-center font-medium',
          },
          {
            value: 'manual',
            display: 'Manual',
            className: 'w-1/2 justify-center font-medium',
          },
        ]}
        className="w-full"
      />

      {selectionMethod === 'manual' && (
        <div className="flex flex-col gap-1">
          <Text variant="sm" weight="medium">
            Select reference examples
          </Text>
          <Badge
            size="sm"
            className="mr-auto"
            color={selectedExampleIds.length > 10 ? 'error' : 'primary'}
          >
            {`${selectedExampleIds.length} selected`}
          </Badge>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Text variant="sm" weight="medium">
          Number of examples to generate
        </Text>
        <Input
          type="number"
          value={numExamplesToGenerate.toString()}
          size="sm"
          onChange={(value) => handleNumberChange(value)}
          isError={!!error}
        />
        {error ? (
          <Text variant="xs" className="text-error">
            {error}
          </Text>
        ) : (
          <Text variant="xs">Select a number between 1 and 10</Text>
        )}
      </div>
      <div className="flex flex-row justify-end gap-2">
        <Button
          type="button"
          color="secondary"
          variant="outlined"
          size="sm"
          onClick={() => {
            setIsNumberSelectorOpen(false);
          }}
        >
          Cancel
        </Button>
        <Tooltip title={disabledMessage}>
          <Button
            type="button"
            onClick={() => {
              if (gatherSecrets(model).some((i) => !secretKeys.has(i))) {
                openSecretsRef.current?.();
                return;
              }
              setIsNumberSelectorOpen(false);
              setIsSyntheticExamplesPaneOpen(true);
            }}
            disabled={isGenerateDisabled}
          >
            Generate
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
